import axios from "axios";
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_MB, checkFileSize, notifyFileTooLarge } from "./fileLimit";
import { ADMIN_TOKEN_KEY, SUPPORT_TOKEN_KEY, getActiveToken } from "./sessionToken";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;
export const FILES_BASE = BACKEND_URL;

// Re-exported from ./fileLimit so existing imports (@/lib/api) keep working.
export { MAX_UPLOAD_BYTES, MAX_UPLOAD_MB, checkFileSize };

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = getActiveToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Universal safety net: block any FormData upload with a file over 5 MB,
  // even if a specific handler forgot to pre-check.
  if (config.data instanceof FormData) {
    for (const value of config.data.values()) {
      if (value instanceof File && value.size > MAX_UPLOAD_BYTES) {
        const mb = (value.size / (1024 * 1024)).toFixed(1);
        notifyFileTooLarge(mb);
        return Promise.reject(new axios.Cancel(`File troppo grande (${mb} MB)`));
      }
    }
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (axios.isCancel(err)) return Promise.reject(err);
    if (err.response?.status === 413) {
      notifyFileTooLarge();
    }
    if (err.response?.status === 403 && err.response?.data?.code === "tenant_disabled") {
      window.dispatchEvent(new Event("subscription-changed"));
    }
    if (err.response?.status === 428 && err.response?.data?.code === "password_change_required") {
      window.dispatchEvent(new Event("password-required"));
    }
    if (err.response?.status === 401) {
      const sent = err.config?.headers?.get?.("Authorization") || err.config?.headers?.Authorization;
      // An old request must never log out the newly restored admin session.
      if (sent === `Bearer ${getActiveToken()}`) {
        if (sessionStorage.getItem(SUPPORT_TOKEN_KEY)) {
          window.dispatchEvent(new Event("impersonation-expired"));
        } else {
          localStorage.removeItem(ADMIN_TOKEN_KEY);
          if (!window.location.pathname.startsWith("/public/") &&
              window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        }
      }
    }
    return Promise.reject(err);
  }
);

export const fileUrl = (id) => (id ? `${FILES_BASE}/api/files/${id}` : null);
export default api;
